import { FoodsIcon, IconMapping, LinearProgress } from "@foodpilot/foods";
import { Box, Button, Divider, Stack, Typography, useTheme } from "@mui/material";
import { t } from "i18next";
import { NavLink } from "react-router";
import { Fragment } from "react/jsx-runtime";
import { useProductScoresContext } from "src/context/Product/Scores/useProductScores";

export type SummaryDataRow = {
    icon: keyof IconMapping;
    title: string;
    completion?: {
        percent: number;
        count: number;
    };
    to: string;
};

export const SummaryData = () => {
    const theme = useTheme();
    const { productSummary } = useProductScoresContext();

    if (!productSummary) {
        return null;
    }

    const summaryData: SummaryDataRow[] = [
        {
            completion: {
                percent: productSummary.product.completions.weightedAverage,
                count: productSummary.product.completions.count,
            },
            icon: "questionnaires",
            title: t("menu.products.products", "Products"),
            to: "../../questionnaire",
        },
    ];

    if (productSummary.ingredients !== null) {
        summaryData.push({
            completion: {
                percent: productSummary.ingredients.completions.weightedAverage,
                count: productSummary.ingredients.completions.count,
            },
            icon: "ingredients",
            title: t("menu.products.ingredients", "Ingredients"),
            to: "../ingredients",
        });
    }

    if (productSummary.packagings !== null) {
        summaryData.push({
            icon: "packaging",
            title: t("packagings"),
            to: "../packagings",
        });
    }

    return summaryData.map((data, index) => {
        const { icon, title, completion, to } = data;
        const isLastItem = index === summaryData.length - 1;
        return (
            <Fragment key={`product-summary-data-row-${index}`}>
                <Stack direction="row" alignItems="center" gap={8} justifyContent="space-between">
                    <Stack flex={1} direction="row" alignItems="center" gap={1.5} minWidth="200px">
                        <Stack
                            sx={{
                                backgroundColor: (theme) => theme.custom.grey[400],
                                borderRadius: theme.spacing(4),
                                padding: theme.spacing(1),
                            }}
                        >
                            <FoodsIcon icon={icon} size={3} />
                        </Stack>
                        <Typography variant="h5">{title}</Typography>
                    </Stack>
                    <Stack
                        flex={2}
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="row"
                        display="flex"
                        gap={4}
                    >
                        {completion !== undefined && (
                            <Box>
                                <Typography variant="body" sx={{ textTransform: "lowercase", textWrap: "nowrap" }}>
                                    {completion.count} {completion.count > 1 ? t("questionnaires") : t("questionnaire")}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                    <Stack flex={2} width="100%">
                        {completion !== undefined && <LinearProgress value={completion.percent} />}
                    </Stack>
                    <Stack flex={1} direction="row" justifyContent="end">
                        <Button
                            component={NavLink}
                            to={to}
                            variant="ternary"
                            sx={{
                                width: "40px",
                                padding: 0,
                                minWidth: "auto",
                            }}
                        >
                            <FoodsIcon icon="arrowRight" size={2} />
                        </Button>
                    </Stack>
                </Stack>
                {isLastItem === false && <Divider />}
            </Fragment>
        );
    });
};
